import {
  type Config,
  useEventIsSplit,
  usePlantConfig,
  useStoppageUpdate
} from '@domain/shifthandover'
import {MAX_ALLOWED_ATTACHMENTS} from '@hconnect/common/consts'
import {useAttachmentFile} from '@hconnect/common/hooks'
import {
  StoppageEvent,
  StoppageEdit as StoppageEditDto
} from '@hconnect/common/stoppages/types/stoppage.types'
import {Attachment, AttachmentFile, StoppageDto as Stoppage} from '@hconnect/common/types'
import {
  getNewlyAttachedFiles,
  getUniqueAttachments,
  getUrlsForRemovedImages
} from '@hconnect/common/utils'
import {LoadingSwitcher} from '@hconnect/uikit/src/lib2'
import {useUpmPlant, useUpmPlantTimezone} from '@hconnect/upm'
import {spawnSuccessToast} from '@hm/fundamental'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import {useHasPermission} from '@hproduce/permissions'
import {StoppageFormContainer, EventAttachmentUpload} from '@widgets/common-shift-event'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

type StoppageEditProps = {
  plantId: string
  'data-test-id'?: string
  stoppage: StoppageEvent
  doClose(original?: Stoppage, newVersion?: Stoppage): void
  onSubmit?(item: Stoppage): void
  onSuccess?(data: Stoppage): void
}

const getDowntimeStoppageCodes = (plantConfig: Config | undefined) => {
  return {
    kilnStoppageCodes: plantConfig?.kilnStoppageCodes ?? [],
    finishMillStoppageCodes: plantConfig?.finishMillStoppageCodes ?? [],
    rawMillStoppageCodes: plantConfig?.rawMillStoppageCodes ?? [],
    alternativeFuelInstallationStoppageCodes:
      plantConfig?.alternativeFuelInstallationStoppageCodes ?? []
  }
}

export const StoppageEdit: React.FC<StoppageEditProps> = ({
  doClose,
  plantId,
  onSubmit,
  onSuccess,
  stoppage,
  ...props
}) => {
  const upmPlant = useUpmPlant({plantId, shouldRaiseError: true})
  const {data: isEventSplit} = useEventIsSplit({plantId, eventId: stoppage.id})
  const timezone = useUpmPlantTimezone(plantId)
  const hasPermission = useHasPermission({plantId, countryId: upmPlant.country})
  const {t, i18n} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const language = i18n.language
  const isMovieAttachmentEnabled = useCommonFeatureFlag(FeatureFlagKey.MovieAttachment)
  const isAutocompleteProcessStage = useCommonFeatureFlag(FeatureFlagKey.AutocompleteProcessStage, {
    plantId
  })
  const isStoppageSubmitSupported = useCommonFeatureFlag(FeatureFlagKey.SubmitStoppage)
  const [uniqueAttachments, setUniqueAttachments] = useState<Attachment[]>(
    getUniqueAttachments(stoppage?.attachments, MAX_ALLOWED_ATTACHMENTS)
  )
  const [files, setFiles] = useState<AttachmentFile[]>([])

  const {data: plantConfig} = usePlantConfig({plantId, language})

  useEffect(() => {
    setUniqueAttachments(getUniqueAttachments(stoppage?.attachments, MAX_ALLOWED_ATTACHMENTS))
    setFiles([])
  }, [stoppage?.attachments])

  const handleFileReady = useCallback((file: AttachmentFile) => {
    setFiles((prev) => {
      if (prev.find((f) => f.id === file.id)) return prev
      return [...prev, file]
    })
  }, [])

  const handleUpdateSuccess = useCallback(
    (data: Stoppage) => {
      spawnSuccessToast(t('stoppage.updateMessage.success'))
      onSuccess?.(data)
    },
    [onSuccess, t]
  )

  const updateAction = useStoppageUpdate(plantId, {
    onSuccess: handleUpdateSuccess
  })
  const {data, isPending: isLoading} = updateAction

  useEffect(() => {
    if (data) {
      onSubmit?.(data)
    }
  }, [onSubmit, data])

  const doSubmit = React.useCallback(
    (item: StoppageEditDto) => {
      const originalAttachments = stoppage.attachments ?? []
      updateAction.mutate({
        ...item,
        attachmentsToRemove: getUrlsForRemovedImages(originalAttachments, files),
        newAttachments: getNewlyAttachedFiles(originalAttachments, files)
      })
    },
    [stoppage.attachments, updateAction, files]
  )

  const stoppaggeCodes = useMemo(() => {
    return getDowntimeStoppageCodes(plantConfig)
  }, [plantConfig])

  return (
    <>
      <LoadingSwitcher isLoading={isLoading}>
        <StoppageFormContainer
          data-test-id={props['data-test-id']}
          isCreateMode={true}
          isAutocompleteProcessStage={!!isAutocompleteProcessStage}
          item={stoppage}
          timezone={timezone}
          doClose={() => doClose()}
          doSubmit={doSubmit}
          plantId={plantId}
          hiddenFields={{stoppageType: true, hasSalesImpact: true}}
          stoppageCodes={stoppaggeCodes}
          /** Note: Stoppage start/end edit is not allowed as it might not stay
           * mapped to the running time duration */
          disabledFields={{
            stoppageStart: true,
            stoppageEnd: true,
            upmDepartmentId: isEventSplit,
            stoppageType: isEventSplit,
            ...(isStoppageSubmitSupported && stoppage.isSubmitted
              ? {
                  upmDepartmentId: true,
                  stoppageCode: true,
                  eventType: true,
                  stoppageDateType: true,
                  stoppageType: true
                }
              : {})
          }}
        >
          <EventAttachmentUpload
            hasPermission={hasPermission}
            setAttachments={setFiles}
            attachments={files}
            isMovieAttachmentEnabled={isMovieAttachmentEnabled}
          />
        </StoppageFormContainer>
      </LoadingSwitcher>
      {uniqueAttachments.map((attachment) => (
        <AttachmentFileLoader
          key={attachment.id}
          attachment={attachment}
          onReady={handleFileReady}
        />
      ))}
    </>
  )
}

const AttachmentFileLoader = ({
  attachment,
  onReady
}: {
  attachment: Attachment
  onReady: (file: AttachmentFile) => void
}) => {
  const file: AttachmentFile = useAttachmentFile(attachment)

  useEffect(() => {
    if (file.preview) {
      onReady(file)
    }
  }, [file, onReady])

  return null
}
